import { Injectable, isDevMode } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { OpenaiAdsJourney } from './openai-ads-journey';
// import { Analytics } from '../analytics';

export interface CurrentVariations {
	enabledVariations: any,
	enabledVariationsList: any,
	enabledExperiments: any,
}

export interface HopePresentationContext {
	h1: string;
	productKey: string;
	program: string;
	keyword: string;
	campaign: string;
	adGroup: string;
	adContent: string;
	renderedIntro: string;
	displayedRate: string;
	freshness: string;
	headingPath: string;
	/** FRU-23397: the rate shown ABOVE the conversation (desktop headline, first-screen rate hero). displayedRate stays the
	 * bubble's own rate, so its history keeps one meaning. */
	headlineRate?: string;
}

@Injectable({
	providedIn: 'root'
})
export class AnalyticsService {
	private readonly openaiJourney = new OpenaiAdsJourney(window, document);

	private currentQuestion = '';
	// private analytics: any;
	public currentUrl = '';
	private enabledVarations : any = {};
	private enabledVarationsList : any = [];
	private enabledExperiments: any = [];
	private experimentsSubject: BehaviorSubject<CurrentVariations> = new BehaviorSubject(
		{
			enabledVariations: this.enabledVarations,
			enabledVariationsList: this.enabledVarationsList,
			enabledExperiments: this.enabledExperiments
		}
	);

	// FRU-21526 tracking gaps (2026-07-12): pilot/arm markers stamped onto every Hope GA4 event so the
	// pilot + A/B are segmentable in GA4. Hope events are gtag.js-direct (bypass GTM-KKV2B5T), so this
	// helper is the only place to populate them. Param names match googleads' registered GA4 dims.
	private hopeGa4Context: { [key: string]: string } = {};
	// FRU-21526 tracking gaps: URL markers are attacker-controllable — cap length + restrict charset
	// to kill high-cardinality / injection into analytics + the lead (CodeRabbit + Codex review of 0cddc75e).
	private static readonly HOPE_ASSISTANT_SOURCES = ['google_ads_direct', 'landing', 'calculator', 'in_form_switch'];
	private static readonly HOPE_ASSISTANT_ENTRIES = ['google_ads_direct', 'v6_tile', 'ask_hope_button', 'ab_formlanding'];
	// The EXACT active Hope A/B experiment cookie — matched exactly (not a family regex) so stale
	// experiment_FRU-21526-lp-ai-box-v4/v5 cookies can't win over v6 (abx review of 55f152c6). Bump on version.
	private static readonly HOPE_ACTIVE_EXPERIMENT = 'FRU-21526-lp-ai-box-v6-2';
	// The EXACT server arm values for the v6 A/B (abx review of d493b68a): hope_variation must be one of
	// these two — a clean 2-value dimension, never any other same-origin cookie string.
	private static readonly HOPE_V6_VARIATIONS = ['original', 'FRU-21526-v6-2'];
	// codex 7/18: FINITE allowlist for the registered GA4 dims — an unknown ?experience=hope-direct-pilot-v999
	// must NOT mint new experiment/variation values (low-cardinality guarantee). Extend deliberately per launch.
	private static readonly HOPE_KNOWN_EXPERIENCES = ['hope-direct-pilot-v1', 'hope-direct-pilot-v6-2'];
	private static sanitizeHopeMarker(raw: any): string {
		if (!raw) { return ''; }
		return raw.toString().replace(/[^A-Za-z0-9._-]/g, '').slice(0, 64);
	}
	// Field-aware allowlist so NO arbitrary high-cardinality value reaches GA4 or the lead (abx review):
	// assistantSource/assistantEntry must be a KNOWN value; experience must be hope-namespaced (<= ~45 chars).
	static hopeMarker(field: 'experience' | 'assistantSource' | 'assistantEntry', raw: any): string {
		const v = AnalyticsService.sanitizeHopeMarker(raw);
		if (!v) { return ''; }
		if (field === 'assistantSource') { return AnalyticsService.HOPE_ASSISTANT_SOURCES.indexOf(v) >= 0 ? v : ''; }
		if (field === 'assistantEntry') { return AnalyticsService.HOPE_ASSISTANT_ENTRIES.indexOf(v) >= 0 ? v : ''; }
		return /^hope[-_][A-Za-z0-9._-]{1,40}$/.test(v) ? v : '';   // experience: hope-namespaced only
	}

	// Pure (no window/document) so it is unit-testable: given a URL query string + a cookie string,
	// return the GA4 marker params. experience/assistantSource/assistantEntry from the query (sanitized;
	// assistantSource allowlisted); hope_variation from the ACTIVE Hope experiment_* cookie only
	// (FRU-21526 family) so stale, over-tagged cookies (FRU-21170/Fast/V4) cannot mislabel the arm.
	static parseHopeGa4Markers(search: string, cookie: string): { [key: string]: string } {
		const found: { [key: string]: string } = {};
		const qs = new URLSearchParams(search || '');
		const experience = AnalyticsService.hopeMarker('experience', qs.get('experience'));
		if (experience) { found['hope_experience'] = experience; }
		const assistantSource = AnalyticsService.hopeMarker('assistantSource', qs.get('assistantSource'));
		if (assistantSource) { found['hope_assistant_source'] = assistantSource; }
		const assistantEntry = AnalyticsService.hopeMarker('assistantEntry', qs.get('assistantEntry'));
		if (assistantEntry) { found['hope_assistant_entry'] = assistantEntry; }
		// hope_variation is the A/B arm, read from the EXACT active v6 cookie — but ONLY via a POSITIVE
		// current-entry gate (abx review of 6f1923ea): read it solely for V6-capable CURRENT sources (the
		// landing tile + the in-form switch, the V6 A/B surfaces). A source-less legacy ?startAssistant=1 or
		// a direct-pilot entry gets NO variation, so a stale 2yr cookie can't attach an arm where the current
		// entry has none. The decoded value is validated against the known v6 arm values (HOPE_V6_VARIATION),
		// not any same-origin cookie string, to keep the dimension low-cardinality.
		// The A/B arm attaches ONLY to the EXACT v6 tile entry: assistantSource=landing AND
		// assistantEntry=v6_tile (abx review of d493b68a). in_form_switch is mixed-origin (also
		// calculator/legacy startAssistant=0), so it is NOT a clean V6 surface and must not inherit a
		// stale V6 arm; a source-less / direct-pilot entry likewise gets no variation.
		const v6TileEntry = found['hope_assistant_source'] === 'landing' && found['hope_assistant_entry'] === 'v6_tile';
		let variation = '';
		if (v6TileEntry) {
			(cookie || '').split(';').forEach(part => {
				const eq = part.indexOf('=');
				if (eq < 0) { return; }
				if (part.slice(0, eq).trim() !== 'experiment_' + AnalyticsService.HOPE_ACTIVE_EXPERIMENT) { return; }
				try {
					const v = AnalyticsService.sanitizeHopeMarker(decodeURIComponent(part.slice(eq + 1).trim()));
					if (v && AnalyticsService.HOPE_V6_VARIATIONS.indexOf(v) >= 0) { variation = v; }
				} catch (e) { /* skip malformed */ }
			});
		}
		if (variation) { found['hope_variation'] = variation; }
		// FRU-21526 GA4 population (googleads handoff 2026-07-17): ALSO emit the REGISTERED GA4 dims
		// `experiment` / `variation` — the hope_* names above are not registered in GA4, so they never
		// reach reports (blank on 100% of hits). Values mirror what the lead record gets. Set only when
		// a marker exists (inert for untagged traffic; control arm of V6 emits variation=original so the
		// A/B segments cleanly). OBSERVATION ONLY per Ed: standard event params, never GA4 key events.
		const directPilotExperience = AnalyticsService.HOPE_KNOWN_EXPERIENCES.indexOf(experience) >= 0;
		if (variation) {
			found['experiment'] = AnalyticsService.HOPE_ACTIVE_EXPERIMENT;
			found['variation'] = variation;
		} else if (directPilotExperience) {
			found['experiment'] = experience.replace(/[-_](?:v\d+(?:[-_]\d+)?|original|FRU[-_]21526[-_]v6[-_]2)$/i, '');   // hope-direct-pilot-v1/v6-2 -> hope-direct-pilot
			found['variation'] = experience;
		}
		return found;
	}

	constructor(private router: ActivatedRoute, private locationService: Location) {
		this.openaiJourney.pageViewed();
		let global_window = (window as any);
		this.captureHopeGa4Context();  // FRU-21526 tracking gaps: snapshot markers before the mid-flow assistantSource URL-strip
		console.log('matomo tests', global_window.matomo_ab);
		//Track enabled variations
		//Get experiments when Matomo loads second using the event listener
		window.addEventListener('ab-variation', (event) => this.trackVariationChangeEvent(event));
		//Get experiments when Matomo loads first using the global array
		if (typeof global_window.matomo_ab != "undefined" && global_window.matomo_ab) {
			//Tests loaded before the event was subscribed, run through them
			console.log('adding in tests added before event load', global_window.matomo_ab);
			global_window.matomo_ab.map((test) => this.trackVariationChange(test));
		}
		// this.analytics = new Analytics();
		this.currentUrl = '/communication' + this.locationService.path(true);
		//If we add different routes we may need to subscribe to router.url or something as well
		//when we upgrade angular we should be able to use the locationService.onUrlChange event instead
		this.router.queryParams.subscribe(params => this.trackUrlChange());
	}

	trackOpenaiPageViewed(): void {
		try { this.openaiJourney.pageViewed(true); } catch (_) { /* Non-fatal. */ }
	}

	trackOpenaiCommittedAnswer(step: string, value: unknown, contact?: { firstName?: unknown; lastName?: unknown }): void {
		try { this.openaiJourney.committed(step, value, contact); } catch (_) { /* Non-fatal. */ }
	}

	prepareOpenaiSubmission(email: unknown): void {
		try { this.openaiJourney.prepareSubmission(email); } catch (_) { /* Non-fatal. */ }
	}

	trackOpenaiAcceptedSubmission(leadId: unknown, email?: unknown): void {
		try { this.openaiJourney.submitted(leadId, email); } catch (_) { /* Non-fatal. */ }
	}

	//Consume ab-variation custom event and track currently enabled variations
	trackVariationChangeEvent(event) {
		if (!event.detail || !event.detail.experimentName || !event.detail.variationName) {
			console.log('Analytics trackVariationChange called incorrectly', event);
			console.log('No event detail?', !event.detail);
			console.log('No event experimentName?', !event.detail.experimentName)
			console.log('No event variationName?', !event.detail.variationName);
			return;
		}
		this.trackVariationChange(event.detail);
	}

	trackVariationChange(change) {
		this.enabledVarations[change.variationName] = true;
		this.enabledVarationsList.push(change.variationName);
		let enabledExperiment = this.enabledExperiments.find((experiment) => experiment.experimentName === change.experimentName);
		if (enabledExperiment === undefined || enabledExperiment === null) {
			this.enabledExperiments.push({
				'experiment' : change.experimentName,
				'variation' : change.variationName
			});
		}
		console.log('enabled experiments', this.enabledExperiments);
		console.log('enabled variations', this.enabledVarations);
		console.log('enabled variations list', this.enabledVarationsList);
		this.experimentsSubject.next({
			enabledVariations: this.enabledVarations,
			enabledVariationsList: this.enabledVarationsList,
			enabledExperiments: this.enabledExperiments
		});
	}

	getExperiments() {
		return this.experimentsSubject.asObservable();
	}

	trackUrlChange() {
		let newUrl = '/communication' + this.locationService.path(true);
		if (this.currentUrl != newUrl) {
			var previousUrl = this.currentUrl;
			this.currentUrl = newUrl;
			let _paq = (window as any)._paq = ((window as any)._paq || []);   // abx seq134 #3: assign back so pre-bootstrap events queue on window._paq (Matomo drains it), not a discarded local array
			if (_paq) {
				_paq.push(['setReferrerUrl', previousUrl]);
				_paq.push(['setCustomUrl', this.currentUrl]);
				this.pushLpTypeDimension();
				_paq.push(['trackPageView']);
			} else {
				console.log('paq not found');
			}
			/*var data = {
				'setReferrerUrl': previousUrl,
				'setCustomUrl': this.currentUrl,
			};
			console.log('url change', data);*/
		}
	}

	trackQuestionShown(detail: string): void {
		this.currentQuestion = detail || '';
		if (detail === 'firstNameLastName') {
			this.trackReachedContact('form', detail);
		}
	}

	// FRU-21494: the classic form already uses URL changes for Matomo page views. These explicit,
	// arm-tagged GA4 events make its steps and committed answers symmetric with Hope without adding a
	// second Matomo page view or inferring form traffic from a missing arm.
	trackFormStep(stepKey: string): void {
		if (!stepKey) { return; }
		this.trackGa4('rate_finder_step', { step: stepKey, arm: 'form' });
		this.trackGa4('rate_finder_step_view', { step: stepKey, arm: 'form' });
	}

	trackFormAnswer(stepKey: string, value: any): void {
		this.trackGa4('rate_finder_answer', {
			step: stepKey || '',
			answer_value: (value ?? '').toString().slice(0, 60),
			arm: 'form',
		});
	}

	trackRateFinderSubmitState(arm: 'assistant' | 'form', state: 'attempt' | 'success' | 'failure', detail: any = {}): void {
		this.trackGa4('rate_finder_submit_' + state, { ...detail, arm });
	}

	// FRU-21436: GA4 event helper (gtag). No-op safely if gtag isn't loaded.
	trackGa4(eventName: string, params: any = {}): void {
		try {
			const g = (window as any).gtag;
			// FRU-21526 tracking gaps: merge the pilot/arm markers into EVERY Hope event (event-specific
			// params win on any collision). Inert for non-pilot traffic (context is empty).
			if (typeof g === 'function') { g('event', eventName, { ...this.hopeGa4Context, ...params }); }
		} catch (e) { /* non-fatal */ }
	}

	// FRU-21526 tracking gaps (2026-07-12): snapshot the pilot/arm markers for GA4. experience/
	// assistantSource/assistantEntry come from the entry URL (read at construction, before the mid-flow
	// assistantSource strip); hope_variation is the A/B arm truth from the server-set experiment_<name>
	// cookie (empty for direct-pilot clicks, which are marked by experience, not an arm). Only keys with
	// a value are set, so non-pilot / non-arm traffic contributes nothing (inert).
	// Public so the chat can refresh when Hope opens (in-form form->Hope switch / late-set arm cookie).
	// captureHopeGa4Context MERGES (never clears), so a post-strip refresh can't blank assistantSource.
	// Re-snapshot when Hope opens (catches a late-set arm cookie). No source-inference fallback -- the
	// regular-form -> Hope switch now tags assistantSource=in_form_switch at the real switch navigation
	// (startAiAssistantFromButton), so a source-less open stays correctly unclassified (abx review of ec17f69a).
	refreshHopeGa4Context(): void { this.captureHopeGa4Context(); }

	private captureHopeGa4Context(): void {
		try {
			const found = AnalyticsService.parseHopeGa4Markers(window.location.search, document.cookie || '');
			// MERGE (never replace): a refresh adds late values (variation) without clearing construction-time
			// ones (assistantSource, which syncLoanPurposeToUrl strips from the URL mid-flow).
			this.hopeGa4Context = { ...this.hopeGa4Context, ...found };
		} catch (e) { /* non-fatal */ }
	}

	// FRU-21436: the AI assistant (chat) does NOT change the URL between questions, so the normal
	// trackUrlChange() Matomo pageview never fires per chat step. Call this on every chat step
	// appearance to give the assistant arm the SAME per-step Matomo pageview + a GA4 event as the
	// form arm, tagged arm=assistant so the A/B can be segmented. Mirrors a step "page" without
	// touching the real URL (so it never collides with the form's URL-driven pageviews).
	trackAssistantStep(stepKey: string): void {
		if (!stepKey) { return; }
		const stepUrl = '/communication/rates-form?aiAssistant=1&step=' + stepKey;
		try {
			const _paq = (window as any)._paq;
			if (_paq) {
				_paq.push(['setReferrerUrl', this.currentUrl]);
				_paq.push(['setCustomUrl', stepUrl]);
				_paq.push(['setDocumentTitle', 'AI Rate Finder - ' + stepKey]);
				this.pushLpTypeDimension();
				_paq.push(['trackPageView']);
				this.currentUrl = stepUrl;
			}
		} catch (e) { /* non-fatal */ }
		this.trackGa4('rate_finder_step', { step: stepKey, arm: 'assistant' });
		if (stepKey === 'firstName' || stepKey === 'firstNameLastName') {
			this.trackReachedContact('assistant', stepKey);
		}
	}

	// FRU-21436: fire on every answer in the assistant arm (chip tap or typed), arm-tagged.
	trackAssistantAnswer(stepKey: string, value: any): void {
		this.trackGa4('rate_finder_answer', { step: stepKey || '', answer_value: (value ?? '').toString().slice(0, 60), arm: 'assistant' });
		try { const _paq = (window as any)._paq; if (_paq) { _paq.push(['trackEvent', 'AIRateFinder', 'answer', stepKey || '']); } } catch (e) { /* non-fatal */ }
	}

	// FRU-21526 (Ed 7/20): step-ENTRY event -> a clean viewed->answered drop-off funnel per step in GA4/Matomo
	// (drop-off = viewed but not answered). Fired at the same points as the server queueAction('step') so the
	// GA4 funnel stays consistent with the chat-action log. arm=assistant for apples-to-apples vs the original form.
	trackAssistantStepView(stepKey: string): void {
		this.trackGa4('rate_finder_step_view', { step: stepKey || '', arm: 'assistant' });
		try { const _paq = (window as any)._paq = ((window as any)._paq || []); _paq.push(['trackEvent', 'AIRateFinder', 'step_view', stepKey || '']); } catch (e) { /* non-fatal */ }
	}

	// FRU-23192 (9/27): what happens INSIDE a typed field, so a viewed-but-not-answered drop can be told apart: never
	// tapped, tapped but never typed, filled by autofill or a keyboard suggestion, or rejected (with the reason). On
	// iPhone Safari only 53% of Hope visitors who see the email question answer it (78% on the original, 9/24-26), and
	// the step's own events cannot say why. Actions: tap, typed, filled, rejected, and from Hope's box hope_box and asked.
	// `detail` is a short code, never what the visitor typed.
	trackAssistantField(stepKey: string, action: string, detail: string = ''): void {
		const d = (detail || '').toString().replace(/[^a-z0-9_]/gi, '').slice(0, 24);
		this.trackGa4('rate_finder_field', { step: stepKey || '', field_action: action || '', detail: d, arm: 'assistant' });
		try { const _paq = (window as any)._paq; if (_paq) { _paq.push(['trackEvent', 'AIRateFinder', 'field_' + (action || ''), (stepKey || '') + (d ? '|' + d : '')]); } } catch (e) { /* non-fatal */ }
	}

	// FRU-21526 (Ed 7/20): returning welcome-card FUNNEL -- card_shown -> yes / edit / dismiss -> yes_submit.
	// Measures how many returning visitors get the short flow, which of the two card buttons (Yes / Edit) they
	// pick, and how many submit on the Yes 2-step path (yes_submit). arm=assistant. (Card is behind the
	// returning_visitor lever. This is unrelated to the old auto/"fast mode".)
	trackReturningFunnel(action: string, returningType: string = ''): void {
		this.trackGa4('hope_returning_funnel', { action: action || '', returning_type: returningType || '', arm: 'assistant' });
		try { const _paq = (window as any)._paq; if (_paq) { _paq.push(['trackEvent', 'AIRateFinder', 'returning_' + (action || ''), returningType || '']); } } catch (e) { /* non-fatal */ }
	}

	// Record WHAT ACTUALLY RENDERED vs WHAT THEY SEARCHED on every direct-Hope document visit. The
	// prior constructor-time event described intended resolver output and duplicated whenever the chat
	// component was recreated. A window sentinel makes this once-per-page-visit; rendered copy/rate,
	// semantic heading path and bounded ad context make the event independently auditable.
	trackHopePresentation(context: HopePresentationContext): boolean {
		try {
			const w: any = window as any;
			if (w.__fruHopePresentationTracked) { return false; }
			w.__fruHopePresentationTracked = true;
		} catch (e) { /* non-browser callers still emit */ }
		const clip = (v: any, n: number, fallback: string = '') => (v || fallback).toString()
			.replace(/[\u0000-\u001f\u007f-\u009f"|]/g, ' ')
			.replace(/\s+/g, ' ')
			.trim()
			.slice(0, n);
		const kw = clip(context.keyword, 90, '(none)');
		const head = clip(context.h1, 90, '(default)');
		const productKey = clip(context.productKey, 40, 'generic');
		const program = clip(context.program, 20);
		const campaign = clip(context.campaign, 60);
		const adGroup = clip(context.adGroup, 80);
		const adContent = clip(context.adContent, 80);
		const renderedIntro = clip(context.renderedIntro, 160);
		const displayedRate = clip(context.displayedRate, 40);
		const freshness = clip(context.freshness, 80);
		const headingPath = clip(context.headingPath, 160);
		const headlineRate = clip(context.headlineRate, 40);
		this.trackGa4('hope_presentation', {
			presentation_h1: head,
			product_key: productKey,
			program,
			keyword: kw,
			campaign,
			ad_group: adGroup,
			ad_content: adContent,
			rendered_intro: renderedIntro,
			displayed_rate: displayedRate,
			rate_freshness: freshness,
			heading_path: headingPath,
			headline_rate: headlineRate,
			arm: 'assistant',
		});
		try {
			const _paq = (window as any)._paq = ((window as any)._paq || []);
			// Put the compact audit keys first: Matomo installations may cap/truncate an event label,
			// and keyword/ad/rate/DOM semantics must survive even when the visible intro is long.
			const ad = [campaign, adGroup, adContent].filter(Boolean).join('/');
			_paq.push(['trackEvent', 'AIRateFinder', 'presentation',
				'kw="' + kw + '"' + (ad ? ' | ad=' + ad : '')
					+ ' => [' + productKey + (program ? '/' + program : '') + '] ' + head
					+ ' | rate=' + (displayedRate || '(none)')
					+ (headlineRate ? ' | hero=' + headlineRate : '')
					+ (freshness ? ' | fresh=' + freshness : '')
					+ (headingPath ? ' | dom=' + headingPath : '')
					+ ' | rendered="' + renderedIntro + '"']);
		} catch (e) { /* non-fatal */ }
		return true;
	}

	/**
	 * FRU-23397: the presentation event stops waiting after about four seconds, so a rate that painted later (a slow
	 * first request, or the retries at about 1.5 and 3.5 seconds) was indistinguishable from none. Record it once per
	 * page, Matomo only, observation only.
	 */
	trackHopePresentationLateRate(rate: string, where: 'bubble' | 'headline', ms: number): boolean {
		try {
			const w: any = window as any;
			if (w.__fruHopeLateRateTracked) { return false; }
			w.__fruHopeLateRateTracked = true;
		} catch (e) { /* non-browser callers still emit */ }
		const r = (rate || '').toString().replace(/[\u0000-\u001f\u007f-\u009f"|]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 40);
		const t = Math.max(0, Math.round(Number(ms) || 0));
		try {
			const _paq = (window as any)._paq = ((window as any)._paq || []);
			_paq.push(['trackEvent', 'AIRateFinder', 'presentation_rate_late', 'rate=' + r + ' | where=' + where + ' | ms=' + t]);
		} catch (e) { /* non-fatal */ }
		return true;
	}

	trackQuestionAnswered(): void {
		if (this.currentQuestion) {
			this.trackGa4('rate_finder_validation', { step: this.currentQuestion, result: 'passed', arm: 'form' });
		}
	}

	trackQuestionAnsweredInvalid(): void {
		if (this.currentQuestion) {
			this.trackGa4('rate_finder_validation', { step: this.currentQuestion, result: 'failed', arm: 'form' });
		}
	}

	trackRedirect(path: string): void {
		// this.analytics.track('redirect', path);
	}

	trackEvent(eventCategory: string,
		eventAction: string,
		eventName: string|null = null,
		eventValue: string|null = null): void {
		let _paq = (window as any)._paq = ((window as any)._paq || []);   // abx seq134 #3: assign back so pre-bootstrap events queue on window._paq (Matomo drains it), not a discarded local array
		if (eventName) {
			if (eventValue) {
				_paq.push(['trackEvent', eventCategory, eventAction, eventName, eventValue]);
			} else{
				_paq.push(['trackEvent', eventCategory, eventAction, eventName]);
			}
		} else {
			_paq.push(['trackEvent', eventCategory, eventAction]);
		}
	}

	// FRU-21436: Matomo goals for the AI assistant — click/open (goal 139) and submit (goal 140),
	// both configured allow_multiple=0 (counted once per visitor).
	// FRU-21436: flip tracking — every form<->assistant switch as a GA4 event + Matomo event,
	// capturing the from-step + direction + a monotonic flip counter, so we can see WHERE people
	// flip to the assistant and back.
	private _flipSeq = 0;
	private _reachedContactTracked = false;

	// FRU-22871: consent-attempt funnel diagnostics (one-to-one consent flow). Privacy-safe mirror
	// of the first-party consent_attempt_event POST: GA4 event `consent_attempt` and a Matomo event
	// (category ConsentAttempt / action stage / label outcome:reason). Params are coarse
	// low-cardinality tokens only — no lead content, no contact data, no URLs. Opt-out is respected
	// here so the first-party diagnostic POST can still run while GA4/Matomo are suppressed.
	trackConsentAttempt(stage: string, outcome: string, reason: string = ''): void {
		const token = (raw: any, limit: number): string =>
			(raw === null || raw === undefined || typeof raw === 'object') ? '' : String(raw).replace(/[^A-Za-z0-9_]/g, '').slice(0, limit);
		const safeStage = token(stage, 48);
		if (!safeStage) { return; }
		if (AnalyticsService.hasFruRemoveCookies()) { return; }
		const safeOutcome = token(outcome, 16) || 'unknown';
		const safeReason = token(reason, 48);
		this.trackGa4('consent_attempt', { stage: safeStage, outcome: safeOutcome, reason: safeReason });
		try {
			const _paq = (window as any)._paq = ((window as any)._paq || []);
			_paq.push(['trackEvent', 'ConsentAttempt', safeStage, safeOutcome + (safeReason ? ':' + safeReason : '')]);
		} catch (e) { /* non-fatal */ }
	}

	private static hasFruRemoveCookies(): boolean {
		try {
			return typeof document !== 'undefined'
				&& document.cookie.split(';').some((cookie) => cookie.trim().split('=')[0] === 'fruRemoveCookies');
		} catch (e) { return false; }
	}

	private trackReachedContact(arm: 'assistant' | 'form', stepKey: string): void {
		if (this._reachedContactTracked) { return; }
		this._reachedContactTracked = true;
		this.trackGa4('funnel_step_reached_contact', { step: stepKey || '', arm });
	}

	trackAssistantSwitch(fromStep: string, direction: 'to_assistant' | 'to_form'): void {
		this._flipSeq += 1;
		try {
				this.trackGa4('rate_finder_mode_switch', {
					from_step: fromStep || '',
					direction,
					flip_seq: this._flipSeq,
					arm: direction === 'to_assistant' ? 'form' : 'assistant',
				});
			const _paq = (window as any)._paq;
			if (_paq) { _paq.push(['trackEvent', 'AIRateFinder', 'switch_' + direction, fromStep || '', this._flipSeq]); }
		} catch (e) { /* non-fatal */ }
	}

	trackAssistantOpen(): void {
		this.trackGa4('funnel_step_engaged_ai', { arm: 'assistant' });
		try { this.trackGoal(139); } catch (e) { /* non-fatal */ }
	}

	trackAssistantSubmit(): void {
		try { this.trackGoal(140); } catch (e) { /* non-fatal */ }
	}

	// FRU-21535: one final-submit boundary for both experiences. The classic form has always
	// recorded Matomo goal 69 immediately before submitLead; Hope used a separate path that only
	// recorded goal 140 (the AI prompt-submit goal), so real Hope leads could outnumber final-submit
	// clicks. Keep the existing FRU-site gate and add the same observation-only GA4 event for both arms.
	trackFinalSubmit(arm: 'assistant' | 'form', attribution?: { [key: string]: string }): void {
		if (Number((window as any).siteId) === 1) {
			this.trackGoal(69);
		}
		this.trackGa4('rate_finder_submit', Object.assign({ arm }, attribution || {}));
	}

	// FRU-21526 (Ed): tag every assistant Matomo request with lp_type (visit-scoped custom dimension 3)
	// so the experiment can be segmented by landing page. page_type is written by the landing into
	// sessionStorage.fru21526AiLandingContext; visit-scoped means once set it sticks to the submit goal.
	private pushLpTypeDimension(): void {
		try {
			const raw = sessionStorage.getItem('fru21526AiLandingContext');
			const ctx = raw ? JSON.parse(raw) : null;
			const _paq = (window as any)._paq;
			if (!_paq) { return; }
			if (ctx && ctx.page_type) { _paq.push(['setCustomDimension', 3, ctx.page_type.toString()]); return; }
			// FRU-23701: with no landing context (the A/B redirect into Hope is a server 302, so no landing script runs), the
			// page itself stamped the dimension from the URL's type= before Matomo loaded (index.html). Keep that value; only a
			// visit with none gets 'default'. A tracker without getCustomDimension sets 'default' as before.
			_paq.push([AnalyticsService.defaultLpTypeUnlessSet]);
		} catch (e) { /* non-fatal */ }
	}

	/** Runs with Matomo's tracker as `this` (a function pushed onto _paq). */
	static defaultLpTypeUnlessSet = function (this: any): void {
		try {
			if (typeof this.getCustomDimension === 'function' && this.getCustomDimension(3)) { return; }
			this.setCustomDimension(3, 'default');
		} catch (e) { /* non-fatal */ }
	};

	trackGoal(goalID: number): void {
		if(isDevMode()) {
			console.log('tracking goal', goalID);
		}
		let _paq = (window as any)._paq = ((window as any)._paq || []);   // abx seq134 #3: assign back so pre-bootstrap events queue on window._paq (Matomo drains it), not a discarded local array
		this.pushLpTypeDimension();
		_paq.push(['trackGoal', goalID]);
		this.sendAssistantOpenGoalFallback(goalID);
	}

	private sendAssistantOpenGoalFallback(goalID: number): void {
		if (goalID !== 139) { return; }
		try {
			const globalWindow = window as any;
			if (globalWindow.__fruAiGoal139FallbackSent) { return; }
			globalWindow.__fruAiGoal139FallbackSent = true;

			const siteId = this.getMatomoSiteId();
			const now = new Date();
			const params = new URLSearchParams();
			params.set('idsite', siteId.toString());
			params.set('rec', '1');
			params.set('idgoal', goalID.toString());
			params.set('action_name', document.title || 'AI Rate Finder');
			params.set('url', window.location.href);
			params.set('rand', Math.floor(Math.random() * 1000000).toString());
			params.set('h', now.getHours().toString());
			params.set('m', now.getMinutes().toString());
			params.set('s', now.getSeconds().toString());
			params.set('send_image', '0');
			const visitorId = this.getMatomoVisitorId(siteId);
			if (visitorId) {
				params.set('_id', visitorId);
				params.set('_idn', '0');
			}

			const formData = new FormData();
			formData.append('action', btoa(params.toString()));
			const endpoint = 'https://ab.freerateupdate.com/mm.php';
			if (navigator.sendBeacon && navigator.sendBeacon(endpoint, formData)) { return; }
			fetch(endpoint, { method: 'POST', mode: 'cors', body: formData, keepalive: true }).catch(() => {});
		} catch (e) { /* non-fatal */ }
	}

	private getMatomoSiteId(): number {
		const hostParts = window.location.hostname.split('.');
		const domain = hostParts.length >= 2 ? hostParts.slice(-2).join('.') : window.location.hostname;
		if (domain === 'freerateupdate.com' || domain === 'homeequitychoice.com' || domain === 'besthomeequityloan.com') { return 1; }
		if (domain === 'airatefinder.com' || domain === 'lowratefinder.ai') { return 9; }
		return 5;
	}

	private getMatomoVisitorId(siteId: number): string {
		const prefix = `_pk_id.${siteId}.`;
		const cookie = document.cookie.split(';').map(item => item.trim()).find(item => item.indexOf(prefix) === 0);
		if (!cookie) { return ''; }
		const value = decodeURIComponent(cookie.split('=').slice(1).join('=') || '');
		return value.split('.')[0] || '';
	}

}
